
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import AdminLayout from "./components/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import AssessmentsList from "./pages/admin/AssessmentsList";
import AssessmentView from "./pages/admin/AssessmentView";
import CreateAssessment from "./pages/admin/CreateAssessment";
import EditAssessment from "./pages/admin/EditAssessment";
import Results from "./pages/admin/Results";
import Users from "./pages/admin/Users";
import OrganizationUsers from "./pages/admin/OrganizationUsers";
import Organizations from "./pages/admin/Organizations";
import Profile from "./pages/admin/Profile";
import AdminLogin from "./pages/admin/Login";
import AssessmentPortal from "./pages/assessment/AssessmentPortal";
import OrganizationTeam from "./pages/admin/OrganizationTeam";
import ResetPassword from "./pages/ResetPassword";
import PremiumResetPassword from "./pages/PremiumResetPassword";
import PremiumRequests from "./pages/admin/PremiumRequests";
import PremiumPortal from "./pages/PremiumPortal";
import PremiumSetup from "./pages/PremiumSetup";
import FullAssessmentSignup from "./pages/FullAssessmentSignup";
import SetupAccount from "./pages/SetupAccount";

// Create a new query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            
            {/* Admin Login Route */}
            <Route path="/admin/login" element={<AdminLogin />} />
            
            {/* Password Reset Route */}
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Account Setup (invited users land here from invitation email) */}
            <Route path="/setup-account" element={<SetupAccount />} />
            
            {/* Premium User Password Reset Route */}
            <Route path="/premium-reset-password" element={<PremiumResetPassword />} />
            
            {/* Full Assessment Portal (authenticated dashboard) */}
            <Route path="/full-assessment" element={<PremiumPortal />} />
            {/* Backward-compatible redirect from old /premium URL */}
            <Route path="/premium" element={<Navigate to="/full-assessment" replace />} />
            
            {/* Premium Setup (legacy magic link landing — kept for in-flight links) */}
            <Route path="/premium-setup" element={<PremiumSetup />} />

            {/* Full Assessment self-serve signup (token-based, from approval email) */}
            <Route path="/full-assessment-signup" element={<FullAssessmentSignup />} />
            
            {/* Protected Admin Routes */}
            <Route path="/admin" element={
              <AdminLayout pageTitle="Dashboard" pageDescription="Overview of your assessments.">
                <Dashboard />
              </AdminLayout>
            } />
            <Route path="/admin/assessments" element={
              <AdminLayout pageTitle="Assessments" pageDescription="Create and manage assessment templates for your users.">
                <AssessmentsList />
              </AdminLayout>
            } />
            <Route path="/admin/assessments/create" element={
              <AdminLayout pageTitle="Create Assessment" pageDescription="Create a new assessment template.">
                <CreateAssessment />
              </AdminLayout>
            } />
            <Route path="/admin/assessments/:id" element={
              <AdminLayout pageTitle="Assessment Details" pageDescription="View and manage assessment details.">
                <AssessmentView />
              </AdminLayout>
            } />
            <Route path="/admin/assessments/:id/edit" element={
              <AdminLayout pageTitle="Edit Assessment" pageDescription="Modify assessment structure and content.">
                <EditAssessment />
              </AdminLayout>
            } />
            <Route path="/admin/results" element={
              <AdminLayout pageTitle="Results" pageDescription="View and analyze all assessment submissions.">
                <Results />
              </AdminLayout>
            } />
            <Route path="/admin/users" element={
              <AdminLayout pageTitle="Platform Users" pageDescription="Manage platform administrator accounts and permissions.">
                <Users />
              </AdminLayout>
            } />
            <Route path="/admin/organization-users" element={
              <AdminLayout pageTitle="Organization Users" pageDescription="Manage all organization members across all organizations.">
                <OrganizationUsers />
              </AdminLayout>
            } />
            <Route path="/admin/organizations" element={
              <AdminLayout pageTitle="Organizations" pageDescription="Manage organizations and team members.">
                <Organizations />
              </AdminLayout>
            } />
            <Route path="/admin/profile" element={
              <AdminLayout pageTitle="Profile Settings" pageDescription="Manage your account information and preferences.">
                <Profile />
              </AdminLayout>
            } />
            <Route path="/admin/team" element={
              <AdminLayout pageTitle="Team Members" pageDescription="Manage your organization's team members and roles.">
                <OrganizationTeam />
              </AdminLayout>
            } />
            <Route path="/admin/premium-requests" element={
              <AdminLayout pageTitle="Full Access Requests" pageDescription="Manage full assessment access requests from users.">
                <PremiumRequests />
              </AdminLayout>
            } />
            
            {/* Assessment Portal Routes */}
            <Route path="/assessment/:slug/:mode" element={<AssessmentPortal />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
